import { useQuery } from '@tanstack/react-query';
import { getSignupTerms } from '../lib/api';
import { Loading, Screen } from '../components/primitives';

/**
 * The tenant's Terms and Privacy Policy, served by the COACH app because the
 * coach app is what the apex domain serves. That makes
 * `https://<domain>/privacy` the canonical public url, which is what an app
 * store listing links to.
 *
 * Same `/auth/terms` text the signup checkbox shows, so there is one copy and an
 * admin edit reaches every surface at once.
 */
export function LegalPage({ kind }: { kind: 'privacy' | 'terms' }) {
  const q = useQuery({
    queryKey: ['signup-terms'],
    queryFn: getSignupTerms,
    staleTime: 5 * 60_000,
  });
  const title = kind === 'privacy' ? 'Privacy Policy' : 'Terms and Conditions';
  const body = kind === 'privacy' ? q.data?.privacyText : q.data?.termsText;
  const version = kind === 'privacy' ? q.data?.privacyVersion : q.data?.termsVersion;

  return (
    <Screen eyebrow="Miracles Protocol" title={title}>
      {q.isPending ? (
        <Loading label="Loading" />
      ) : q.isError ? (
        <p className="px-5 py-8 text-[0.93rem] text-ink/70">
          This document could not be loaded. Please try again later.
        </p>
      ) : body ? (
        <div className="px-5 py-6">
          <p className="eyebrow">Version {version}</p>
          <div className="mt-4 whitespace-pre-wrap text-[0.93rem] leading-relaxed text-ink/85">
            {body}
          </div>
        </div>
      ) : (
        // Explicit rather than blank: white space reads to a store reviewer as a
        // broken page, not as a document nobody has written yet.
        <p className="px-5 py-8 text-[0.93rem] leading-relaxed text-ink/70">
          This document has not been published yet. Please contact support before relying on it.
        </p>
      )}
    </Screen>
  );
}
